"use client";

import Link from "next/link";
import { useMemo } from "react";

import { SafeRemoteImage } from "@/components/blog/SafeRemoteImage";
import { resolveArticleAuthor } from "@/lib/blog/authors";
import { formatBlogDateDdMmYyyy } from "@/lib/blog/formatBlogDate";
import { getPostEnrichment } from "@/lib/blog/enrichment";
import { postsBySlug, allPostSlugs } from "@/lib/blog/posts";

const FEATURED_SLUGS = [
  "fifo-fefo-pharmacy-inventory",
  "fmcg-stock-analytics-dashboard",
  "barcode-scanning-receiving-process",
];

export function NewBlogFeatured() {
  const featuredArticles = useMemo(() => {
    const articles: { slug: string; post: (typeof postsBySlug)[string] }[] = [];

    for (const slug of FEATURED_SLUGS) {
      if (postsBySlug[slug]) {
        articles.push({ slug, post: postsBySlug[slug] });
      }
    }

    if (articles.length < 3) {
      const recent = [...allPostSlugs]
        .sort((a, b) => new Date(postsBySlug[b].publishedAt).getTime() - new Date(postsBySlug[a].publishedAt).getTime())
        .slice(0, 8);

      for (const slug of recent) {
        if (!articles.some((a) => a.slug === slug) && postsBySlug[slug]) {
          articles.push({ slug, post: postsBySlug[slug] });
          if (articles.length >= 3) break;
        }
      }
    }

    return articles.slice(0, 3);
  }, []);

  return (
    <section className="ed-blog-featured" aria-labelledby="ed-blog-featured-heading">
      <div className="ed-blog-featured__inner">
        <div className="ed-blog-featured__head">
          <h2 id="ed-blog-featured-heading">Latest on the shelf</h2>
          <p>Practical posts for calmer stock flow, fewer surprises, and confident shelf decisions.</p>
        </div>

        <div className="ed-blog-featured__grid">
          {featuredArticles.map(({ slug, post }) => {
            const enc = getPostEnrichment(slug);
            const author = resolveArticleAuthor(post);
            const dateStr = formatBlogDateDdMmYyyy(post.publishedAt);

            return (
              <article className="ed-blog-featured-card" key={slug}>
                <Link href={`/blog/${slug}`} className="ed-blog-featured-card__link">
                  <div className={`ed-blog-featured-card__media${enc?.heroImage ? "" : " ed-blog-featured-card__media--fallback"}`}>
                    {enc?.heroImage ? (
                      <SafeRemoteImage
                        src={enc.heroImage}
                        alt={post.title}
                        fill
                        sizes="(max-width: 640px) 100vw, 33vw"
                        className="ed-blog-index-card__img"
                        style={{ objectFit: "cover" }}
                      />
                    ) : null}
                  </div>
                  <div className="ed-blog-featured-card__body">
                    <p className="ed-blog-featured-card__cat">{post.category}</p>
                    <h3 className="ed-blog-featured-card__title">
                      <span>{post.title}</span>
                    </h3>
                    <p className="ed-blog-featured-card__excerpt">{post.description}</p>
                    <div className="ed-blog-featured-card__foot">
                      <div>
                        <strong>{author.name}</strong>
                        <br />
                        {dateStr}
                      </div>
                      <span className="ed-blog-featured-card__cta">Read →</span>
                    </div>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
